import moment from 'moment-timezone';
import { DATE_STYLES, formatMoment } from 'utils/dateFormats';

// Matches time-only strings like "11:00", "11:00 AM", "14:30", "2:30 PM"
const TIME_ONLY_REGEX = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i;

/**
 * Parses a time-only string and returns a Date object with today's date.
 *
 * @param {string} timeString - Time string like "11:00 AM" or "14:30"
 * @returns {Date|null} Date object or null if parsing fails
 */
const parseTimeString = timeString => {
    const match = timeString.match(TIME_ONLY_REGEX);
    if (!match) return null;

    let [, hours, minutes, , period] = match;
    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10);

    // Convert 12h to 24h format if AM/PM is present
    if (period) {
        const isPM = period.toUpperCase() === 'PM';
        if (isPM && hours !== 12) hours += 12;
        if (!isPM && hours === 12) hours = 0;
    }

    const dateObj = new Date();
    dateObj.setHours(hours, minutes, 0, 0);
    return dateObj;
};

/**
 * Formats a date in the account's display format (HUMAN_DATE / HUMAN_DATE_TIME
 * styles, e.g. "Jan 21, 2026" or "Jan 21, 2026 at 2:30 PM"); time-only values
 * use the TIME style ("2:30 PM").
 *
 * @param {string|Date|number} date - The date to format, or a time-only string when onlyTime is true
 * @param {Object} [options] - Configuration options
 * @param {boolean} [options.includeTime=false] - Whether to include time in date output
 * @param {boolean} [options.onlyTime=false] - When true, treats input as time-only string (e.g., "11:00 AM")
 * @param {string} [options.timezone] - IANA timezone (e.g., "America/New_York").
 *                                       Defaults to browser's local timezone.
 * @param {boolean} [options.isUtc=false] - When true, parses the input via `moment.utc(...)`
 *                                          so naive timestamp strings are anchored in UTC
 *                                          before the timezone conversion. Only enable this
 *                                          when the caller knows its source emits naive UTC
 *                                          strings (e.g. `Y-m-d H:i:s` from a path that bypasses
 *                                          Eloquent datetime casting). Enabling for arbitrary
 *                                          formats (e.g. `MM/DD/YYYY`) re-anchors them to UTC
 *                                          midnight and can shift the calendar day for users
 *                                          west of UTC.
 * @returns {string} Formatted date string, or '-' if date is falsy or invalid
 */
export const formatDate = (date, options = {}) => {
    if (!date) return '-';

    const { includeTime = false, onlyTime = false, timezone, isUtc = false } = options;

    // Handle time-only input
    if (onlyTime) {
        const dateObj = typeof date === 'string' ? parseTimeString(date.trim()) : null;

        if (!dateObj) return '-';

        return formatMoment(timezone ? moment(dateObj).tz(timezone) : moment(dateObj), DATE_STYLES.TIME);
    }

    const dateObj = isUtc ? moment.utc(date).toDate() : new Date(date);

    if (isNaN(dateObj.getTime())) return '-';

    const momentDate = timezone ? moment(dateObj).tz(timezone) : moment(dateObj);

    return formatMoment(momentDate, includeTime ? DATE_STYLES.HUMAN_DATE_TIME : DATE_STYLES.HUMAN_DATE);
};
